import {
	IShareService, 
	IInheritedShareableStore, 
	IShareServiceFactory,
	ClientLinkShare,
	ServerLinkShare,
	Activity
} from 'sharebase/domain';
import {
	IDocumentService,
	MediaService,
	ISubscriptionsService
} from 'sharebase/services';
import {
	AuthService,
	PhoenixToggleService,
	ActionService
} from 'phoenix/services';
import {
	DocumentView,
	DocumentRevision,
	pxItemTypeGuards,
	ShareBaseDocument,
	ShareBaseFolder,
	PhoenixSubscription,
	ShareEntity,
	RightsType,
	RightsOption,
	AdvancedOption,
	Acl,
	PhoenixActivity,
	ObjectType
} from 'phoenix/domain';
import { AclDatastore } from 'sharebase/dataStores/aclDatastore';
import { DocumentAction } from 'sharebase-app/components/documentView/documentView.actions';
import { DocumentDetails } from 'sharebase-app/components/documentDetails/documentDetails';
import { 
	DocViewerFormat,
	DocViewerMode 
} from 'phoenix/components/documentViewer/documentViewer';

const DefaultDocumentDetails: DocumentDetails = {
	activities: null,
	comments: null,
	dateCreated: null,
	dateModified: null,
	fileFormat: null,
	fileSize: null,
	owner: null,
	versions: null
}

interface DocumentPromiseCache {
	activities:ng.IPromise<Activity[]>,
	details:ng.IPromise<DocumentDetails>,
	load:ng.IPromise<any>,
	notificationSettings:ng.IPromise<PhoenixSubscription>
}

export type DocumentPage = {
	Id: number;
	Url: string;
};

/**
* @ngdoc service
* @name  sharebase-app.sbaDocumentDataStore
* 
* @description 
* A datastore used to interact with the contents of a Document.
* 
**/
export class DocumentDataStore implements IInheritedShareableStore {
	public static $inject = [
		'$q',
		'pxAuthService',
		'pxActionService',
		'pxPhoenixToggleService',
		'sbDocumentService',
		'sbDocumentDetailService',
		'sbShareServiceFactory',
		'sbAclDatastore',
		'sbMediaService',
		'sbSubscriptionsService',
		'sbaLibraryDataStore'
	];

	constructor(
		private $q:ng.IQService,
		private pxAuthService:AuthService,
		private pxActionService:ActionService,
		private pxPhoenixToggleService:PhoenixToggleService,
		private sbDocumentService:IDocumentService,
		private sbDocumentDetailService,
		private sbShareServiceFactory:IShareServiceFactory,
		private sbAclDatastore:AclDatastore,
		private sbMediaService:MediaService,
		private sbSubscriptionsService:ISubscriptionsService,
		private sbaLibraryDataStore
	) {
		this.shareService = this.sbShareServiceFactory.create(this.sbDocumentService, this);
		this._details = DefaultDocumentDetails;
		this.pxActionService.subscribe<ShareBaseDocument>(DocumentAction.Save, doc => {
			if(doc.DocumentId === this.itemId){
				this._details.versions.push(toRevision(doc));
			}
		});
	}

	private shareService: IShareService;
	private _document: DocumentView = null;
	private _pageCount: number = null;
	private _pages: DocumentPage[] = null;
	private _breadcrumbs = null;
	private _shares:Acl[] = [];
	private _inheritedShares:Acl[] = [];
	private _links:ClientLinkShare[] = [];
	private _inheritedLinks:ClientLinkShare[] = [];
	private _viewerMode:DocViewerMode;
	private _viewerFormat:DocViewerFormat;
	private _isSharedContent = false;
	private _details:DocumentDetails;
	private _subscriptionSettings:PhoenixSubscription = null;
	private _cachedPromises:DocumentPromiseCache = {
		activities: null,
		details: null,
		load: null,
		notificationSettings: null
	};
	private _documentId:number = null;

	public get itemId() {
		return this._documentId;
	};
	
	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#isSharedContent
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 *
	 * @description
	 * Whether or not the document is shared with the current user.        
	 *
	 * @returns {boolean} Flag determining if the document is shared.
	 */
	get isSharedContent() {
		return this._isSharedContent;
	}

	get details() {
		return this._details;
	}
	
	get owner() {
		return this.sbAclDatastore.ownerAcl;
	}

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#type
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 *
	 * @description
	 * PX_OBJECT_TYPES enum designation of the entities contained within.
	 * 
	 * @returns {int} PX_OBJECT_TYPES enum value.
	 */
	public get type() {
		return ObjectType.DOCUMENT;
	}

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#mimeType
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 * 
	 * @description
	 * Get the mimetype of the loaded document
	 */
	public get mimeType() {
		if (!this._document) {
			return '';
		}

		return this._document.Embedded.Document.MimeType;
	}

	public textContent:string;

	/**
	 * @ngdoc method
	 * @name sbaDocumentDataStore#load
	 * @methodOf sharebase-app.sbaDocumentDataStore
	 * @kind function
	 *
	 * @description
	 * Loads a document
	 * 
	 * @param {object} documentId A Document ID
	 * @param {boolean} ignoreCache ignore cached promise, default false
	 *
	 * @returns {Deferred} A promise that will resolve with a selected document
	 */
	public load(documentId:number, ignoreCache = false) {
		// Only load personal library if this is NOT a share by link user.
		// If they are, this will cause a 401 error and redirect the user
		// to the login page.
		if(this._cachedPromises.load && documentId === this.itemId && !ignoreCache){
			return this._cachedPromises.load;
		}

		this.clearStore();

		let libPromise = this.$q.resolve();

		if (!this.pxAuthService.isShareByLinkUser) {
			libPromise = !this.sbaLibraryDataStore.myLibrary ?
			this.sbaLibraryDataStore.loadLibrary() : 
			this.$q.resolve(this.sbaLibraryDataStore.myLibrary);
		}
		
		// the document ID should be assigned immediately as it is used when looking for cached data
		this._documentId = documentId;
		return this._cachedPromises.load = libPromise
			.then(() => this.sbDocumentService.getDocument(documentId))
			.then(docView => {
				this._documentId = documentId;
				this.setDocument(docView);
			
				// check to see if the folder is in user's library
				this._isSharedContent = this.pxAuthService.isShareByLinkUser || this.sbaLibraryDataStore.myLibrary.LibraryId !== this._document.Embedded.Document.LibraryId;
				return this.setViewerSettings(this._document);
			})
			.then(() => {
				let p:ng.IPromise<any> = this._viewerFormat === DocViewerFormat.TXT ?
					this.sbDocumentService
						.getContent(this._document, this._viewerFormat)
						.then(content => this.textContent = content) :
					this.$q.resolve();			
				

				return p.then(() => this._document);
			})
			.catch(e => {
				// roll back to previous ID on a failed load
				this._documentId = this._document ? this._document.Embedded.Document.DocumentId : null;	
				throw e;
			});
	}

	public getDocumentUri(page:number, section?:number, maxWidth?:number){
		return this.sbDocumentService.getDocumentUri(this._document, page, section, this.viewFormat, maxWidth);
	}

	public rename(newName:string){
		return this.sbDocumentService
			.renameDocument(this._document.Embedded.Document, newName)
			.then(renamedDoc => this._document.Embedded.Document = renamedDoc);
	}

	public getMediaInfo(){
		this.guardDocument();
		return this.sbDocumentService.getMediaInfo(this._document.Embedded.Document)
			.then(function(mediaInfo){
				return mediaInfo;
			})
	}

	public getCurrentUserAcl(): ng.IPromise<Acl> {
		this.guardDocument();
		return this.sbAclDatastore.load(this._document.Embedded.Document);
	}

	public getLinkRights(userAcl:Acl) {
		return this.shareService.getLinkRights(userAcl);
	}

	private setDocument(docView:DocumentView) {			
		this._document = docView;
		this._pageCount = docView.PageCount;
		this._breadcrumbs = docView.Embedded.Document.Embedded.Path ? docView.Embedded.Document.Embedded.Path : null;
		this._pages = [];

		for (var i = 1; i <= docView.PageCount; i++) {
			var page = {
				Id: i,
				Url: docView.PageTemplate.replace(docView.PageTemplateParam, i.toString())
			};
			this._pages.push(page);
		}
	}

	public getPermissions(share:Acl) {
		this.guardDocument();
		return this.getCurrentUserAcl()
			.then(myAcl => this.shareService.getRights(myAcl, share));			
	}

	public getShares() {
		this.guardDocument();
		return this.shareService.getShares(this._document.Embedded.Document);
	}

	public getLinks(){
		this.guardDocument();
		return this.shareService.getLinks(this._document.Embedded.Document);
	}

	public createShare(entity: ShareEntity, right:RightsType, advanced:AdvancedOption[]) {
		this.guardDocument();
		return this.shareService.createShare(this._document.Embedded.Document, entity, right, advanced);
	}

	public createShares(entities:ShareEntity[], right:RightsType, advanced:AdvancedOption[]) {
		this.guardDocument();
		return this.shareService.createShares(this._document.Embedded.Document, entities, right, advanced);
	}
	
	public createLink(share:ServerLinkShare, right:RightsOption) {	
		this.guardDocument();	
		return this.shareService.createLink(this._document.Embedded.Document, share, right);
	}

	public revokeLink(revokedLink:ClientLinkShare){
		if(!revokedLink.Links.RevokeUri){ 
			throw new Error('Invalid link object.');
		}

		return this.shareService.revokeLink(revokedLink).then(() => revokedLink);
	}

	public updateShare(updatedShare:Acl, right:RightsType, advanced:AdvancedOption[]) {
		this.guardDocument();
		return this.shareService.updateShare(this._document.Embedded.Document, updatedShare, right, advanced);
	}

	public deleteShare(deletedShare:Acl): ng.IPromise<Acl[]> {
		this.guardDocument();
		return this.shareService.deleteShare(this._document.Embedded.Document, deletedShare);
	}

	public guardDocument() {
		if(!angular.isDefined(this._document) || this._document === null) {
			throw new Error('No document loaded.');
		}
	}

	private setViewerSettings(document:DocumentView){
		this._viewerMode =  DocViewerMode.MULTIPAGE;
		this._viewerFormat = DocViewerFormat.IMG;
		let p = this.$q.resolve();
		switch(document.Embedded.Document.FileFormat){
			case 'Microsoft Excel Spreadsheet':
				this._viewerMode =  DocViewerMode.TABBED;
				this._viewerFormat = DocViewerFormat.SVG;
				break;
			case 'Microsoft PowerPoint Presentation':
				this._viewerMode =  DocViewerMode.SLIDE;
				this._viewerFormat = DocViewerFormat.SVG;
				break;
			case 'Microsoft Word Document':
			case 'Rich Text Document':
				this._viewerFormat = DocViewerFormat.SVG;
				break;
			case 'Markdown':
				p = this.pxPhoenixToggleService.isEnabled('Core/InlineEdit').then(enabled =>{
					if(enabled){
						this._viewerFormat = DocViewerFormat.TXT;
					}	
				});
				break;
			case 'Image':					
				this._viewerMode = document.PageCount === 1 ? DocViewerMode.PICTURE : DocViewerMode.MULTIPAGE;
				break;
		}

		return p;
	}

	public deleteDocument(document: ShareBaseDocument | DocumentRevision, purge:boolean = false) {
		if (pxItemTypeGuards.isShareBaseDocument(document)) {
			return this.sbDocumentService.deleteDocument(document, purge);
		} else {
			let i = this._details.versions.indexOf(document);
			if(i >= 0){
				this._details.versions.splice(i, 1);
			}

			return this.sbDocumentService.deleteRevision(document);
		}
	}

	public promote(rev:DocumentRevision): ng.IPromise<any> {
		return this.sbDocumentService.promote(rev)
			.then((res) => {
				this._details.versions.push(res);
				return res;
			});
	}

	public copyDocument(document: ShareBaseDocument | ShareBaseDocument[], destination: ShareBaseFolder) {
		if (!Array.isArray(document)) {
			document = [document];
		}
		return this.$q.all<ShareBaseDocument>(
			document.map(doc => this.sbDocumentService.moveDocument(doc, destination, true))
		);
	}

	public moveDocument(document: ShareBaseDocument, destination: ShareBaseFolder) {
		return this.sbDocumentService
			.moveDocument(document, destination, false)
			.then(() => {
				return this.load(document.DocumentId);
			})
			.then(docView => docView.Embedded.Document);
	}

	public clearStore() {
		this._isSharedContent = false;
		this._details = DefaultDocumentDetails;
		this._document = this._documentId = this._subscriptionSettings = this._pageCount = this._pages = this._viewerFormat = this._viewerMode = null;
		this._shares = [];
		this._inheritedShares = [];
		this.textContent = '';
		Object.keys(this._cachedPromises).forEach(k => {
			this._cachedPromises[k] = null;
		});
	}

	public messageSharedUsers(subject:string, message:string) {
		return this.shareService.sendShareMessage(this._document.Embedded.Document,subject, message);
	}

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#pageCount
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 *
	 * @description
	 * A given document's page count         
	 *
	 * @returns {int} A count of a selected document's pages
	 */
	public get pageCount() { return this._pageCount; };

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#viewMode
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 *
	 * @description
	 * A given document view mode         
	 *
	 * @returns {int} Integer of the view mode
	 */
	public get viewMode() { return this._viewerMode; };

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#viewFormat
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 *
	 * @description
	 * A given document view format         
	 *
	 * @returns {int} Integer of the view format
	 */
	public get viewFormat() { return this._viewerFormat; };

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#pages
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 *
	 * @description
	 * A given document's pages
	 * 
	 * @returns {array} An array of a selected document's pages
	 */
	public get pages() { return this._pages; };

	/**
	* @ngdoc property
	* @name sbaDocumentDataStore#breadcrumbs
	* @methodOf sharebase-app.sbaDocumentDataStore
	* @kind property
	*
	* @description
	* A given documents path.
	* 
	* @returns {array} An an array folders.
	* 
	*/
	public get breadcrumbs() { return this._breadcrumbs; }
	
	/**
	* @ngdoc property
	* @name sbaDocumentDataStore#shares
	* @methodOf sharebase-app.sbaDocumentDataStore
	* @kind property
	*
	* @description
	* A list of users or groups this document is directly shared with, if any.
	* 
	* @returns {array} An an array of shared users or groups.
	* 
	*/
	public get shares() { return this._shares; }
	public set shares(shares) { this._shares = shares;}
	
	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#inheritedShares
	 * @methodOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 *
	 * @description
	 * A list of users or groups this library is indirectly shared with, if any.
	 * 
	 * @returns {array} An an array of shared users or groups.
	 * 
	 */
	public get inheritedShares() { return this._inheritedShares; }
	public set inheritedShares(iShares) { this._inheritedShares = iShares; }

	/**
	* @ngdoc property
	* @name sbaDocumentDataStore#links
	* @methodOf sharebase-app.sbaDocumentDataStore
	* @kind property
	*
	* @description
	* A list of links this document is directly shared with, if any.
	* 
	* @returns {array} An an array of shared links.
	* 
	*/
	public get links() { return this._links; }
	public set links(links) {this._links = links;}
	
	/**
	* @ngdoc property
	* @name sbaDocumentDataStore#inheritedLinks
	* @methodOf sharebase-app.sbaDocumentDataStore
	* @kind property
	*
	* @description
	* A list of inherited links this document is directly shared with, if any.
	* 
	* @returns {array} An an array of inherited shared links.
	* 
	*/
	public get inheritedLinks() { return this._inheritedLinks; }
	public set inheritedLinks(iLinks) { this._inheritedLinks = iLinks; }

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#parentFolder
	 * @methodOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 * 
	 * @description
	 * If a document has been loaded, returns the folder that the document lives
	 * in, otherwise it returns null
	 * 
	 * @returns {Object} The folder the document lives in
	 */
	public get parentFolder() {
		if (!this._document) {
			return null;
		}
		else {
			let path = this._document.Embedded.Document.Embedded.Path;
			return path && path[path.length - 1];
		}
	}
		
	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#document
	 * @methodOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 *
	 * @description
	 * Get the document that is loaded in the document view.
	 * Returns null if no document has been loaded.
	 */
	public get document(): ShareBaseDocument {
		if (!this._document) {
			return null;
		}
		else {
			return this._document.Embedded.Document;
		}
	}

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#isMedia
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 * 
	 * @description
	 * Check if the document is a video or audio document.
	 * 
	 * @returns {boolean} True if the document is a video or audio document.
	 */
	public get isMedia(): boolean {
		if (!this._document) {
			return false;
		}
		else {
			return this.sbMediaService.isMediaDocument(this._document.Embedded.Document);
		}
	}

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#documentView
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 * 
	 * @description
	 * Get the document view object for the current document.
	 * 
	 * @returns {DocumentView} The document view object or null if one has not been loaded.
	 */
	public get documentView(): DocumentView {
		return this._document;
	}

	/**
	 * @ngdoc property
	 * @name sbaDocumentDataStore#extension
	 * @propertyOf sharebase-app.sbaDocumentDataStore
	 * @kind property
	 * 
	 * @description
	 * Get the extension (without the leading .) for the current document. 
	 */
	public get extension(): string {
		if (!this._document) {
			return '';
		}
		else {
			let docNameParts = this._document.Embedded.Document.DocumentName.split('.');
			return docNameParts.length <= 1 ? '' : docNameParts[docNameParts.length - 1];
		}
	}

	public getDetails() {
		this.guardDocument();

		if(this._cachedPromises.details){
			return this._cachedPromises.details;
		}
		else {
			let doc = this._document.Embedded.Document;
			let baseDetails = {
				dateCreated: doc.DateCreated as string,
				dateModified: doc.DateModified as string,
				fileFormat: doc.FileFormat,
				fileSize: doc.FileSize,
				owner: doc.Owner
			};
	
			return this._cachedPromises.details = this.$q.all([
				this.sbDocumentDetailService.getComments(doc.DocumentId),
				this.sbDocumentDetailService.getVersions(doc.DocumentId)
			])
			.then((responses:any[][]) => {
				return angular.merge(baseDetails, {
					comments: responses[0],
					versions: responses[1]
				}) as DocumentDetails;
			})
			.then(details => this._details = details);
		}
	}

	public getActivities() {
		this.guardDocument();

		if(this._cachedPromises.activities){
			return this._cachedPromises.activities;
		}
		else {
			let doc = this._document.Embedded.Document;
			
			return this._cachedPromises.activities = this.sbDocumentDetailService
				.getActivities(doc.DocumentId)
				.then((activities:PhoenixActivity[]) => {
					return this._details.activities = activities.map(activity => new Activity(activity));
				});	
		}
	}

	public getNotificationSettings() {
		this.guardDocument();

		if(this._cachedPromises.notificationSettings){
			return this._cachedPromises.notificationSettings;
		}
		else {
			let docId = this.itemId;
			return this._cachedPromises.notificationSettings = this.sbSubscriptionsService
				.getDocumentSettings(docId)
				.then((settings:PhoenixSubscription) =>	this._subscriptionSettings = settings);	
		}
	}
}


function toRevision(doc:ShareBaseDocument){
	return {
		AsyncPurgeOpStatus: "None",
		AsyncTrashOpStatus: "None",
		OriginalFileName: doc.DocumentName,
		CreatedBy: doc.Owner,
		DateCreated: doc.DateCreated,
		Icon: doc.Icon,
		MimeType: doc.MimeType,
		VersionNumber: doc.VersionNumber,
		Hash: doc.Embedded.LatestFile.Hash,
		FileSize: doc.FileSize,
		Links: {
			SelfUri: doc.Links.Self
		}
	} as DocumentRevision;
}


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/documentDataStore.ts